import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Order } from '@api/Orders/Order';
import { showAlertWarning } from '@utils/Alerts/Message';

/**
 * El pedido seleccionado en `/orders`: cuál es, cómo se abre desde una
 * notificación (`?orderId=`) y cómo se mantiene al día con el listener.
 * No lee Firestore: todo sale de `orders`, que ya llegó por la ventana realtime.
 */
const useSelectedOrder = (orders: Order[]) => {
    const navigation = useNavigate();
    const [currentOrder, setCurrentOrder] = useState<Order | undefined>(undefined);

    /**
     * Abrir el pedido que venía en la notificación (`?orderId=…`).
     *
     * ⚠️ **Se lee de `location.search` en cada cambio, NO una vez al montar.**
     * La primera versión guardaba el parámetro en un `useRef` inicializado en el
     * primer render, y eso fallaba justo en el caso más común: **si ya estás en
     * la pantalla de pedidos, navegar a la misma ruta con otro query NO remonta
     * el componente**, así que el ref conservaba su valor viejo y el pedido
     * nuevo nunca se abría (reportado por Irving el 2026-08-29).
     *
     * Se espera a que las órdenes lleguen —el listado es realtime y arranca
     * vacío— y cada `orderId` se procesa **una sola vez**: sin eso, cada
     * snapshot le cambiaría la selección al usuario bajo la mano.
     */
    const location = useLocation();
    const pedidoAbiertoRef = useRef<string>('');
    useEffect(() => {
        const buscado = new URLSearchParams(location.search).get('orderId');
        if (!buscado || !orders.length) return;
        if (pedidoAbiertoRef.current === buscado) return;
        pedidoAbiertoRef.current = buscado;
        const encontrado = orders.find((order) => order.id === buscado);
        // Se limpia con el router (no con `history.replaceState`) para que
        // `location` quede consistente: si no, el efecto seguiría viendo el
        // parámetro viejo en el próximo cambio de órdenes.
        navigation(location.pathname, { replace: true });
        if (encontrado) {
            setCurrentOrder(encontrado);
        } else {
            // Pasa si el pedido ya se completó y salió del listado, o si es de
            // otra sucursal. Decirlo es mejor que dejar la lista como si nada.
            showAlertWarning(
                'Ese pedido ya no está en la lista. Puede que ya se haya completado o sea de otra sucursal.'
            );
        }
    }, [location.search, location.pathname, orders, navigation]);

    // Link de seguimiento del delivery manual, SEPARADO de `currentOrder`.
    //
    // ⚠️ `currentOrder` se reemplaza ENTERO con cada entrega del listener (el
    // efecto sobre `orders` de abajo), y en cocina el tablero recibe entregas
    // todo el día. Cuando el link se guardaba tecleándolo dentro de
    // `currentOrder`, el primer snapshot lo borraba y "En ruta" fallaba con "No
    // hay un link de seguimiento" aunque el campo se viera lleno (reporte de la
    // operación, 2026-09-07). El borrador se reinicia al cambiar de pedido o
    // cuando cambia el link YA GUARDADO; una entrega sin cambios no lo toca.
    const [routeUrlDraft, setRouteUrlDraft] = useState<string>('');
    useEffect(() => {
        setRouteUrlDraft(currentOrder?.routeUrl ?? '');
    }, [currentOrder?.id, currentOrder?.routeUrl]);

    useEffect(() => {
        if (currentOrder?.id && orders.length > 0) {
            const updatedOrder = orders.find((o) => o.id === currentOrder.id);

            if (updatedOrder) {
                setCurrentOrder(updatedOrder);
            }
        }
    }, [orders]);

    return { currentOrder, setCurrentOrder, routeUrlDraft, setRouteUrlDraft };
};

export default useSelectedOrder;
